// VisitorCheckout.jsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Paper, Typography, Button, Box, CircularProgress, Alert, Grid, Card, CardContent, Chip
} from '@mui/material';
import { CheckCircle, Warning } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const VisitorCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const visitId = new URLSearchParams(location.search).get('visitId');

  useEffect(() => {
    const fetchVisit = async () => {
      if (!visitId) {
        setErrorMessage('Visit ID is missing.');
        setLoading(false);
        return;
      }

      try {
        const visitDoc = await getDoc(doc(db, 'visit_approvals', visitId));
        if (!visitDoc.exists()) {
          setErrorMessage('Visit not found.');
          setLoading(false);
          return;
        }

        const visitData = visitDoc.data();
        console.log('Visit document data:', visitData);

        if (visitData.visitorUserID !== user?.uid) {
          setErrorMessage('Access Denied: This visit does not belong to your account.');
          setLoading(false);
          return;
        }

        setVisit({ id: visitDoc.id, ...visitData });
      } catch (error) {
        console.error('Error fetching visit:', error);
        setErrorMessage('Failed to load visit information.');
      } finally {
        setLoading(false);
      }
    };

    fetchVisit();
  }, [visitId, user?.uid]);

  const handleCheckout = async () => {
    if (!visitId) return;

    try {
      const newCheckoutTime = new Date(); // Local checkout time for UI
      await updateDoc(doc(db, 'visit_approvals', visitId), {
        status: 'concluded',
        checkOutTime: serverTimestamp(),
      });

      setVisit((prev) => ({
        ...prev,
        status: 'concluded',
        checkOutTime: { seconds: Math.floor(newCheckoutTime.getTime() / 1000) },
      }));

      setSuccessMessage('You have successfully checked out!');
      setTimeout(() => navigate('/visitor-dashboard'), 3000);
    } catch (error) {
      console.error('Checkout error:', error);
      setErrorMessage('Checkout failed. Please try again.');
    }
  };

  const calculateVisitDuration = () => {
    if (!visit?.visitorTime?.seconds) return '—';
    const checkIn = new Date(visit.visitorTime.seconds * 1000);
    const checkOut = visit?.checkOutTime?.seconds
      ? new Date(visit.checkOutTime.seconds * 1000)
      : new Date(); // Use current time if checkout not done
    const diff = checkOut - checkIn;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading visit details...</Typography>
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Warning sx={{ fontSize: 64, color: 'error.main' }} />
        <Typography variant="h5" color="error" gutterBottom>{errorMessage}</Typography>
        <Button variant="contained" onClick={() => navigate('/visitor-dashboard')}>
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom textAlign="center">
        Visitor Checkout
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <CheckCircle sx={{ verticalAlign: 'middle', mr: 1 }} />
          {successMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Visit Summary</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Name:</strong> {visit?.visitorName || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Email:</strong> {visit?.visitorEmail || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography><strong>Purpose:</strong> {visit?.purpose || visit?.visitReason || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Check-in:</strong> 
                {visit?.visitorTime?.seconds 
                  ? new Date(visit.visitorTime.seconds * 1000).toLocaleString() 
                  : '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Check-out:</strong> 
                {visit?.checkOutTime?.seconds
                  ? new Date(visit.checkOutTime.seconds * 1000).toLocaleString()
                  : new Date().toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography><strong>Visit Duration:</strong></Typography>
              <Chip label={calculateVisitDuration()} color="primary" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!visit?.checkOutTime && (
        <Box sx={{ textAlign: 'center' }}>
          <Button variant="contained" color="primary" onClick={handleCheckout}>
            Complete Checkout
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default VisitorCheckout;
